import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { convertToUUID } from 'uuid-encoding';
import { Icon, Flex, Modal, Button, H4, IconButton } from 'design-system-web';
import { Loading } from 'components';
import { H5, S, P } from 'components/typography';
import { Thumbnail } from 'components/general';
import { Chip, ChipStyle } from 'components/ads';
import {
  ICON_SET_FONTAWESOME,
  FONT_AWESOME_REGULAR,
  SHORT_TIME_WITH_TZ,
  SIMPLE_DATE,
} from 'constants-activate';
import { ApprovalStatus } from 'graphql-query-contracts';
import { getTimeZoneByGeoLocation, dateFormatter } from 'async-timing';
import { getImageUrl } from 'application-types';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { rrulestr } from 'rrule';
import { parseRRuleSchedule } from 'lane-shared/domains/visitorManagement/helpers/parseRRuleSchedule';
import {
  usePendingApprovals,
  useSubmissionVisitorApproval,
} from 'react-hooks';
import type { SubmissionVisitor } from 'react-hooks';
import type { Channel, UserType } from 'application-types';
import { WatchlistMatchCards, ApprovalActionButtons } from '../../../../components';
import type { WatchlistMatch } from '../../../../components';
import { formatFullName } from 'formatters';

import styles from './styles.scss';

export type ConfirmationActionType = 'approve' | 'deny';

export interface ConfirmationModalState {
  isOpen: boolean;
  actionType: ConfirmationActionType | null;
  submissionVisitorId: string;
  visitorName: string;
  isRecurring: boolean;
}

interface PendingApprovalComponentProps {
  user: UserType;
  channel: Channel;
  searchParams: Record<string, unknown>;
  updateSearchParams: (props: Record<string, unknown>) => void;
}

const TRANSLATION_KEYS = {
  tabTitle: 'web.admin.channel.visitor.log.tab.pending.approval',
  visitDetails: 'web.admin.channel.visitor.log.pendingApproval.visitDetails',
  host: 'web.admin.channel.visitor.log.pendingApproval.host',
  visitorName: 'web.admin.channel.visitor.log.columns.visitor',
  visitorEmail: 'web.admin.channel.visitor.log.columns.visitorEmail',
  visitorCompany: 'web.admin.channel.visitor.log.columns.visitorCompany',
  visitorPhone: 'web.admin.channel.visitor.log.columns.visitorPhoneNumber',
  date: 'web.admin.channel.visitor.log.columns.date',
  timeWindow: 'web.admin.channel.visitor.log.pendingApproval.timeWindow',
  status: 'web.admin.channel.visitor.log.columns.status',
  recurrenceSchedule: 'web.admin.channel.visitor.log.pendingApproval.recurrenceSchedule',
  viewRecurrenceDetails: 'web.admin.channel.visitor.log.pendingApproval.viewRecurrenceDetails',
  approveSuccess: 'web.admin.channel.visitor.log.pendingApproval.approve.success',
  denySuccess: 'web.admin.channel.visitor.log.pendingApproval.deny.success',
  approveError: 'web.admin.channel.visitor.log.pendingApproval.approve.error',
  denyError: 'web.admin.channel.visitor.log.pendingApproval.deny.error',
  emptyState: 'web.admin.channel.visitor.log.pendingApproval.emptyState',
  modalApproveTitle: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.title',
  modalApproveDescription: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.description',
  modalApproveDescriptionRecurring: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.description.recurring',
  modalDenyTitle: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.title',
  modalDenyDescription: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.description',
  modalDenyDescriptionRecurring: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.description.recurring',
  modalReturnToPage: 'web.admin.channel.visitor.log.pendingApproval.modal.button.returnToPage',
  actionApprove: 'web.admin.channel.visitor.log.pendingApproval.action.approve',
  actionDeny: 'web.admin.channel.visitor.log.pendingApproval.action.deny',
};

const initialModalState: ConfirmationModalState = {
  isOpen: false,
  actionType: null,
  submissionVisitorId: '',
  visitorName: '',
  isRecurring: false,
};

export function PendingApprovalComponent({
  channel,
}: PendingApprovalComponentProps) {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ConfirmationModalState>(initialModalState);

  const isWatchlistV2Enabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistV2,
    false
  );

  const timeZone = getTimeZoneByGeoLocation({
    latitude: channel?.address?.geo[1],
    longitude: channel?.address?.geo[0],
  });

  const organizationId = convertToUUID(channel?._id);

  const {
    pendingVisitors,
    loading,
    refetch,
  } = usePendingApprovals({
    organizationId
  });

  const { approvalLoading, handleApproval } = useSubmissionVisitorApproval({
    organizationId,
    onSuccess: refetch,
  });

  const openConfirmationModal = (
    actionType: ConfirmationActionType,
    submissionVisitorId: string,
    visitorName: string,
    isRecurring: boolean
  ) => {
    setModalState({
      isOpen: true,
      actionType,
      submissionVisitorId,
      visitorName,
      isRecurring,
    });
  };

  const closeConfirmationModal = () => {
    setModalState(initialModalState);
  };

  const onApprovalAction = async (
    submissionVisitorId: string,
    status: ApprovalStatus,
    visitorName: string
  ) => {
    const isApproval = status === ApprovalStatus.ApprovalStatusApproved;
    const success = await handleApproval(submissionVisitorId, status);

    closeConfirmationModal();

    if (success) {
      const successKey = isApproval
        ? TRANSLATION_KEYS.approveSuccess
        : TRANSLATION_KEYS.denySuccess;
        
      window.Toast.show(t(successKey, { name: visitorName }), 3000);
    } else {
      const errorKey = isApproval
        ? TRANSLATION_KEYS.approveError
        : TRANSLATION_KEYS.denyError;

      window.Toast.show(t(errorKey), 5000);
    }
  };

  const handleConfirmAction = async () => {
    if (!modalState.actionType) return;

    const status = modalState.actionType === 'approve'
      ? ApprovalStatus.ApprovalStatusApproved
      : ApprovalStatus.ApprovalStatusRejected;

    await onApprovalAction(
      modalState.submissionVisitorId,
      status,
      modalState.visitorName
    );
  };

  const getModalTitle = () => {
    if (modalState.actionType === 'approve') {
      return t(TRANSLATION_KEYS.modalApproveTitle);
    }

    return t(TRANSLATION_KEYS.modalDenyTitle);
  };

  const getModalDescription = () => {
    if (modalState.actionType === 'approve') {
      return modalState.isRecurring
        ? TRANSLATION_KEYS.modalApproveDescriptionRecurring
        : TRANSLATION_KEYS.modalApproveDescription;
    }

    return modalState.isRecurring
      ? TRANSLATION_KEYS.modalDenyDescriptionRecurring
      : TRANSLATION_KEYS.modalDenyDescription;
  };

  const getConfirmButtonText = () => {
    if (modalState.actionType === 'approve') {
      return t(TRANSLATION_KEYS.actionApprove);
    }

    return t(TRANSLATION_KEYS.actionDeny);
  };

  if (!isWatchlistV2Enabled) {
    return null;
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" className={styles.loadingContainer}>
        <Loading />
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" gap={6} className={styles.pendingApprovalContainer}>
        {pendingVisitors.length === 0 ? (
          <EmptyState />
        ) : (
          pendingVisitors.map((submissionVisitor) => {
            const visitorName = formatFullName(
              submissionVisitor.visitorPass?.visitor?.firstName ?? '',
              submissionVisitor.visitorPass?.visitor?.lastName ?? ''
            );
            const isRecurring = Boolean(submissionVisitor.recurrence?.schedule);

            return (
              <VisitorEntryCard
                key={submissionVisitor.id}
                submissionVisitor={submissionVisitor}
                timeZone={timeZone}
                onApprove={() => openConfirmationModal('approve', submissionVisitor.id, visitorName, isRecurring)}
                onDeny={() => openConfirmationModal('deny', submissionVisitor.id, visitorName, isRecurring)}
                isLoading={approvalLoading}
              />
            );
          })
        )}
      </Flex>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeConfirmationModal}
        testId="approvalConfirmationModal"
        className={styles.confirmationModal}
        includeHeader={false}
      >
        <Flex className={styles.confirmationModalHeader} justify="space-between" mb={5}>
          <H4 mt={2}>{getModalTitle()}</H4>
          <IconButton
            type={FONT_AWESOME_REGULAR}
            onClick={closeConfirmationModal}
            icon="times"
            data-test="closeButton"
            size="medium"
          />
        </Flex>
        <Flex direction="column" gap={5}>
          <S variant="secondary" mb={5}>
            <Trans i18nKey={getModalDescription()} components={{ strong: <strong /> }} />
          </S>
          <Flex gap={4}>
            <Button
              variant="primary"
              onClick={handleConfirmAction}
              testId="confirm-button"
              size="large"
              loading={approvalLoading}
            >
              {getConfirmButtonText()}
            </Button>
            <Button
              variant="secondary"
              onClick={closeConfirmationModal}
              testId="cancel-button"
              size="large"
            >
              {t(TRANSLATION_KEYS.modalReturnToPage)}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation();

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      className={styles.emptyStateContainer}
    >
      <Icon
        name="check-circle"
        set={ICON_SET_FONTAWESOME}
        size="large"
        className={styles.emptyStateIcon}
      />
      <P className={styles.emptyStateText}>{t(TRANSLATION_KEYS.emptyState)}</P>
    </Flex>
  );
}

interface VisitorEntryCardProps {
  submissionVisitor: SubmissionVisitor;
  timeZone: string;
  onApprove: () => void;
  onDeny: () => void;
  isLoading: boolean;
}

function VisitorEntryCard({
  submissionVisitor,
  timeZone,
  onApprove,
  onDeny,
  isLoading,
}: VisitorEntryCardProps) {
  const visitorPass = submissionVisitor.visitorPass;
  const visitor = visitorPass?.visitor;
  const watchlists = submissionVisitor.watchlists || [];

  const visitorName = formatFullName(visitor?.firstName ?? '', visitor?.lastName ?? '');

  const formatTimeWindow = () => {
    if (!visitorPass?.startDatetime || !visitorPass?.endDatetime) return '-';

    const start = dateFormatter(visitorPass.startDatetime, SHORT_TIME_WITH_TZ, timeZone);
    const end = dateFormatter(visitorPass.endDatetime, SHORT_TIME_WITH_TZ, timeZone);

    return `${start} - ${end}`;
  };

  const formatDate = () => {
    if (!visitorPass?.startDatetime) return '-';

    return dateFormatter(visitorPass.startDatetime, SIMPLE_DATE, timeZone);
  };

  const getRecurrenceText = () => {
    if (!submissionVisitor.recurrence?.schedule) return null;

    try {
      const rrule = rrulestr(submissionVisitor.recurrence.schedule);
      const parsed = parseRRuleSchedule(
        submissionVisitor.recurrence.schedule,
        submissionVisitor.recurrence.scheduleDuration || ''
      );

      const startDate = parsed?.scheduleStartDatetime
        ? dateFormatter(new Date(parsed.scheduleStartDatetime).toISOString(), SIMPLE_DATE, timeZone)
        : '';
      const endDate = parsed?.scheduleEndDate
        ? dateFormatter(new Date(parsed.scheduleEndDate).toISOString(), SIMPLE_DATE, timeZone)
        : '';

      const rruleText = rrule.toText().split('until')[0].trim();
      const capitalizedRruleText = rruleText.charAt(0).toUpperCase() + rruleText.slice(1);

      return {
        text: `${capitalizedRruleText} between ${startDate} to ${endDate}`,
        recurrenceId: submissionVisitor.recurrence.recurrenceId,
      };
    } catch {
      return null;
    }
  };

  const recurrenceInfo = getRecurrenceText();

  const mapWatchlistsToMatches = (): WatchlistMatch[] => {
    return watchlists.map((watchlist) => ({
      id: watchlist.id,
      organizationId: watchlist.organizationId,
      visitorMatchingInfo: {
        firstName: watchlist.visitorMatchingInfo.firstName ?? null,
        lastName: watchlist.visitorMatchingInfo.lastName ?? null,
        email: watchlist.visitorMatchingInfo.email ?? null,
        phone: watchlist.visitorMatchingInfo.phone ?? null,
      },
      severityLevel: watchlist.severityLevel,
      notes: watchlist.notes,
      attachmentIds: watchlist.attachmentIds,
    }));
  };

  return (
    <Flex gap={6} className={styles.visitorEntryCard}>
      <Flex direction="column" gap={4} className={styles.visitDetailsSection}>
        <VisitDetailsCard
          visitorName={visitorName}
          visitorEmail={visitor?.email}
          visitorCompany={visitor?.company}
          visitorPhone={visitor?.phone}
          date={formatDate()}
          timeWindow={formatTimeWindow()}
          recurrenceInfo={recurrenceInfo}
        />
        <HostCard
          hostName={visitorPass?.hostName}
          hostEmail={visitorPass?.host?.profile?.email}
          hostPhone={visitorPass?.host?.profile?.phone}
          hostImage={getImageUrl(visitorPass?.host?.profile?.image)}
          companyName={visitorPass?.tenant?.name || visitorPass?.tenantName}
          companyLogo={getImageUrl(visitorPass?.tenant?.profile?.logo || visitorPass?.tenant?.profile?.image)}
          companyAddress={formatCompanyAddress(visitorPass?.tenant?.address)}
          floor={visitorPass?.floor}
        />
        <ApprovalActionButtons
          onApprove={onApprove}
          onDeny={onDeny}
          isLoading={isLoading}
        />
      </Flex>
      <Flex direction="column" gap={4} className={styles.watchlistMatchSection}>
        {watchlists.length > 0 && (
          <WatchlistMatchCards watchlists={mapWatchlistsToMatches()} />
        )}
      </Flex>
    </Flex>
  );
}

interface VisitDetailsCardProps {
  visitorName: string;
  visitorEmail?: string;
  visitorCompany?: string;
  visitorPhone?: string;
  date: string;
  timeWindow: string;
  recurrenceInfo: { text: string; recurrenceId: string } | null;
}

function VisitDetailsCard({
  visitorName,
  visitorEmail,
  visitorCompany,
  visitorPhone,
  date,
  timeWindow,
  recurrenceInfo,
}: VisitDetailsCardProps) {
  const { t } = useTranslation();

  return (
    <Flex direction="column" className={styles.visitDetailsCard}>
      <Flex justify="space-between" align="center" className={styles.cardHeader}>
        <H5>{t(TRANSLATION_KEYS.visitDetails)}</H5>
        <Chip
          value={t('web.admin.channel.visitor.log.status.pending')}
          size="md"
          type={ChipStyle.Pink}
        />
      </Flex>
      <div className={styles.visitDetailsGrid}>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.visitorName)}</S>
          <P className={styles.detailValue}>{visitorName || '-'}</P>
        </Flex>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.visitorEmail)}</S>
          <P className={styles.detailValue}>{visitorEmail || '-'}</P>
        </Flex>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.visitorCompany)}</S>
          <P className={styles.detailValue}>{visitorCompany || '-'}</P>
        </Flex>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.visitorPhone)}</S>
          <P className={styles.detailValue}>{visitorPhone || '-'}</P>
        </Flex>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.date)}</S>
          <P className={styles.detailValue}>{date}</P>
        </Flex>
        <Flex direction="column" gap={1} className={styles.detailRow}>
          <S className={styles.detailLabel}>{t(TRANSLATION_KEYS.timeWindow)}</S>
          <P className={styles.detailValue}>{timeWindow}</P>
        </Flex>
      </div>
      {recurrenceInfo && (
        <Flex direction="column" className={styles.recurrenceSection}>
          <S className={styles.recurrenceLabel}>
            {t(TRANSLATION_KEYS.recurrenceSchedule)}
          </S>
          <Flex align="center" gap={2} className={styles.recurrenceInfo}>
            <Icon
              name="repeat"
              set={ICON_SET_FONTAWESOME}
              type={FONT_AWESOME_REGULAR}
              className={styles.recurrenceIcon}
            />
            <span>{recurrenceInfo.text}</span>
          </Flex>
          <span className={styles.viewRecurrenceLink}>
            {t(TRANSLATION_KEYS.viewRecurrenceDetails)}
          </span>
        </Flex>
      )}
    </Flex>
  );
}

interface HostCardProps {
  hostName?: string;
  hostEmail?: string;
  hostPhone?: string;
  hostImage?: string;
  companyName?: string;
  companyLogo?: string;
  companyAddress?: string;
  floor?: string;
}

function HostCard({
  hostName,
  hostEmail,
  hostPhone,
  hostImage,
  companyName,
  companyLogo,
  companyAddress,
  floor,
}: HostCardProps) {
  const { t } = useTranslation();

  const hostDetails = [hostEmail, hostPhone].filter(Boolean).join(' | ');
  const addressWithFloor = [companyAddress, floor ? `${floor} Fl` : null]
    .filter(Boolean)
    .join(', ');

  return (
    <Flex direction="column" className={styles.hostCard}>
      <Flex className={styles.hostHeader}>
        <H5>{t(TRANSLATION_KEYS.host)}</H5>
      </Flex>
      <Flex className={styles.hostItemContainer}>
        <Flex align="center" className={styles.hostItem}>
          <Thumbnail
            className={styles.thumbnail}
            name={hostName || '-'}
            src={hostImage}
          />
          <Flex direction="column" className={styles.textWrapper}>
            <P>{hostName || '-'}</P>
            <S>{hostDetails || '-'}</S>
          </Flex>
        </Flex>
        <Flex align="center" className={styles.hostItem}>
          <Thumbnail
            className={styles.thumbnail}
            name={companyName || '-'}
            src={companyLogo}
          />
          <Flex direction="column" className={styles.textWrapper}>
            <P>{companyName || '-'}</P>
            <S>{addressWithFloor || '-'}</S>
            {floor && (
              <S>
                {t(
                  'web.content.feature.visitorManagement.receipt.hostInformation.floor',
                  { floor }
                )}
              </S>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

function formatCompanyAddress(address?: {
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
}): string {
  if (!address) return '';

  const parts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
  ].filter(Boolean);

  return parts.join(', ');
}
